import { OverlayRef } from '@angular/cdk/overlay';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ModalConfig, ModalSize } from './modal.config';

export class ModalRef<T, R = any> {
  /** Subject for notifying the user that the modal has finished closing. */
  private readonly _onClose = new Subject<R | undefined>();
  public onClose$ = this._onClose.asObservable();

  private readonly _onInterceptClose = new Subject<void>();
  /** Subject for notifying the user that the modal has attempted to close. Must be used by the component responsible for opening the modal */
  public onInterceptClose$ = this._onInterceptClose.asObservable();

  /** The instance of component opened into the dialog. */
  public componentInstance!: T;

  private _sizeMap: Record<ModalSize, string> = {
    sm: '300px',
    md: '500px',
    lg: '800px',
    xl: '900px',
  };

  constructor(private _overlayRef: OverlayRef, private _config: ModalConfig) {
    _overlayRef.detachments().subscribe(() => {
      this._overlayRef.dispose();
    });

    _overlayRef.backdropClick().subscribe(() => {
      if (!this._config.disableClose) {
        this.canClose();
      }
    });

    _overlayRef
      .keydownEvents()
      .pipe(
        filter((event) => {
          return event.key === 'Escape' && !this._config.disableClose;
        })
      )
      .subscribe((event) => {
        event.preventDefault();
        this.canClose();
      });
  }

  close(message?: R) {
    this._onClose.next(message);
    this._onClose.complete();
    this._overlayRef.detach();
  }

  /**
   * Allows the user to intercept the close event, and if not intercepted, closes the modal.
   * If the user intercept the event, it is their responsibility to call `close` on the `ModalRef` instance.
   */
  canClose(message?: R) {
    this._onInterceptClose.next();

    if (!this._config.interceptClose) {
      this.close(message);
    }
  }

  resize(size: ModalSize) {
    this._overlayRef.updateSize({ width: this._sizeMap[size] });
  }
}
